import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, DownloadSimple, X, CaretLeft, CaretRight, SortAscending, Shuffle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getMediaUrl } from '../lib/media';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import { fetchOnce } from '../lib/eventCache';
import ReconnectingScreen from '../components/ReconnectingScreen';
import BackButton from '../components/BackButton';
import EventDropAttribution from '../components/EventDropAttribution';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SORTS = [
  { id: 'newest', label: 'Newest' },
  { id: 'oldest', label: 'Oldest' },
  { id: 'shuffle', label: 'Shuffle' },
];

const GuestGallery = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { event: recoveredEvent, status: recoveryStatus, retry: retryRecovery } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  const [event, setEvent] = useState(null);
  const notFound = recoveryStatus === 'failed';
  const [photos, setPhotos] = useState([]);
  const [sort, setSort] = useState('newest');
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    if (recoveryStatus !== 'ready' || !recoveredEvent?.event_id) return;
    setEvent(recoveredEvent);
    // fetchOnce — shares the prefetcher's in-flight /photos request OR
    // serves the 60s-fresh cached value. Instant for returning guests.
    fetchOnce(`${recoveredEvent.event_id}:photos`, () =>
      axios.get(`${API}/events/${recoveredEvent.event_id}/photos`).then((r) => r.data)
    ).then((p) => setPhotos(p || [])).catch(() => {});
  }, [recoveryStatus, recoveredEvent]);

  // Sort pipeline
  const sortedPhotos = useMemo(() => {
    const arr = [...photos];
    if (sort === 'newest') {
      arr.sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''));
    } else if (sort === 'oldest') {
      arr.sort((a, b) => (a.uploaded_at || '').localeCompare(b.uploaded_at || ''));
    } else if (sort === 'shuffle') {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
    }
    return arr;
  }, [photos, sort]);

  const current = lightboxIdx != null ? sortedPhotos[lightboxIdx] : null;

  const openAt = (idx) => setLightboxIdx(idx);
  const close = useCallback(() => setLightboxIdx(null), []);
  const next = useCallback(() => setLightboxIdx((i) => (i == null ? i : (i + 1) % sortedPhotos.length)), [sortedPhotos.length]);
  const prev = useCallback(() => setLightboxIdx((i) => (i == null ? i : (i - 1 + sortedPhotos.length) % sortedPhotos.length)), [sortedPhotos.length]);

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIdx == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, close, next, prev]);

  // Touch swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchMove  = (e) => { touchEndX.current = e.touches[0].clientX; };
  const onTouchEnd   = () => {
    const dx = touchEndX.current - touchStartX.current;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next(); else prev();
    touchStartX.current = 0; touchEndX.current = 0;
  };

  const download = async (photo) => {
    try {
      const res = await fetch(getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`));
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const ext = photo.content_type?.startsWith('video/') ? 'mp4' : 'jpg';
      link.download = photo.original_filename || `moment.${ext}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Downloading...');
    } catch { toast.error('Download failed'); }
  };

  if (notFound) return <ReconnectingScreen status="failed" onRetry={retryRecovery} dashboardHref="/" slug={slug} activeEvent={activeEvent} />;
  if (!event) return <ReconnectingScreen status={recoveryStatus === 'restoring' ? 'restoring' : 'reconnecting'} slug={slug} activeEvent={activeEvent} />;

  return (
    <div className="min-h-screen bg-[#15161B] text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <BackButton fallbackHref={`/event/${slug}/guest`} label="Back" variant="subtle" testId="gallery-back-btn" />
          <div className="flex-1">
            <h1 className="text-2xl font-black tracking-tight">{event.event_name}</h1>
            <p className="text-gray-500 text-sm">{photos.length} moments</p>
          </div>
        </div>

        {/* Sort pills */}
        {photos.length > 1 && (
          <div className="flex items-center gap-2 mb-5" data-testid="gallery-sort-row">
            {SORTS.map((s) => {
              const active = sort === s.id;
              const Icon = s.id === 'shuffle' ? Shuffle : SortAscending;
              return (
                <button
                  key={s.id}
                  onClick={() => setSort(s.id)}
                  className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition-all ${
                    active
                      ? 'bg-white text-[#0F1014] scale-105'
                      : 'bg-white/5 text-gray-400 border border-white/10 hover:border-white/20 hover:text-white'
                  }`}
                  data-testid={`gallery-sort-${s.id}`}
                >
                  <Icon size={12} weight="bold" />
                  {s.label}
                </button>
              );
            })}
          </div>
        )}

        {photos.length === 0 ? (
          <div className="text-center py-20 text-gray-500">No moments yet. Be the first to upload!</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="gallery-grid">
            {sortedPhotos.map((photo, idx) => {
              const isVideo = photo.content_type?.startsWith('video/');
              return (
                <div
                  key={photo.photo_id}
                  className={`relative group rounded-xl overflow-hidden cursor-pointer ${isVideo ? 'col-span-2 aspect-video' : 'aspect-square'}`}
                  onClick={() => openAt(idx)}
                  data-testid={`gallery-tile-${idx}`}
                >
                  {isVideo ? (
                    <video src={getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                  ) : (
                    <img src={getMediaUrl(photo.storage_path, `${API}/photos/${photo.storage_path}`)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  )}
                  {isVideo && <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-bold text-white bg-[#B89762]/80">VIDEO</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {current && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex flex-col"
          onClick={close}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          data-testid="gallery-lightbox"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs text-gray-400 font-mono" data-testid="lightbox-counter">
              {lightboxIdx + 1} / {sortedPhotos.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); download(current); }}
                className="px-3 h-9 rounded-xl text-xs font-semibold text-white border border-white/15 hover:bg-white/5 flex items-center gap-1.5"
                data-testid="lightbox-download"
              >
                <DownloadSimple size={14} weight="bold" />
                Save
              </button>
              <button onClick={(e) => { e.stopPropagation(); close(); }} className="p-2 rounded-full bg-white/10 text-white" data-testid="lightbox-close">
                <X size={18} weight="bold" />
              </button>
            </div>
          </div>

          {/* Media viewport — full available area, portrait-aware */}
          <div className="flex-1 relative flex items-center justify-center px-2 min-h-0" onClick={(e) => e.stopPropagation()}>
            {current.content_type?.startsWith('video/') ? (
              <video
                key={current.photo_id}
                src={getMediaUrl(current.storage_path, `${API}/photos/${current.storage_path}`)}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-full rounded-xl"
                style={{ width: 'auto', height: 'auto', objectFit: 'contain' }}
                onClick={(e) => e.stopPropagation()}
                data-testid="lightbox-video"
              />
            ) : (
              <img
                key={current.photo_id}
                src={getMediaUrl(current.storage_path, `${API}/photos/${current.storage_path}`)}
                alt=""
                className="max-w-full max-h-full rounded-xl select-none"
                style={{ width: 'auto', height: 'auto', objectFit: 'contain' }}
                onClick={(e) => e.stopPropagation()}
                draggable={false}
                data-testid="lightbox-image"
              />
            )}

            {/* Prev / Next arrows (desktop) */}
            {sortedPhotos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all"
                  aria-label="Previous"
                  data-testid="lightbox-prev"
                >
                  <CaretLeft size={22} weight="bold" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all"
                  aria-label="Next"
                  data-testid="lightbox-next"
                >
                  <CaretRight size={22} weight="bold" />
                </button>
              </>
            )}
          </div>

          {/* Metadata footer */}
          <div
            className="px-5 py-3 flex-shrink-0 text-center text-[11px] text-gray-500"
            onClick={(e) => e.stopPropagation()}
          >
            {current.uploaded_at ? new Date(current.uploaded_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : null}
            <span className="hidden sm:inline"> · Use ← → keys or swipe to navigate</span>
          </div>
        </div>
      )}
      <EventDropAttribution />
    </div>
  );
};

export default GuestGallery;
